import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Instagram, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/i18n";
import { fileUrl, isVideo } from "@/api";
import { WidgetEmbed } from "@/components/WidgetEmbed";

const Thumb = ({ src, alt }) =>
  isVideo(src) ? (
    <video src={fileUrl(src)} muted playsInline preload="metadata" className="aspect-square object-cover w-full" />
  ) : (
    <img src={fileUrl(src, 400)} alt={alt} loading="lazy" decoding="async" className="aspect-square object-cover w-full img-bw" />
  );

export const Artists = ({ artists, portfolio, settings }) => {
  const { t, pick } = useLanguage();
  const [active, setActive] = useState(null);
  const [work, setWork] = useState(null);
  const works = active ? portfolio.filter((p) => p.artist_id === active.id) : [];
  const current = work !== null ? works[work] : null;

  return (
    <section id="artists" className="px-4 sm:px-8 lg:px-16 py-20 sm:py-28" data-testid="artists-grid">
      <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-zinc-500 mb-3">01 —</p>
      <h2 className="font-display uppercase text-5xl sm:text-7xl tracking-wide">{t("artists_title")}</h2>
      <p className="text-zinc-400 mt-3 max-w-xl">{t("artists_subtitle")}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-12">
        {artists.map((a, i) => (
          <motion.button key={a.id} onClick={() => setActive(a)} data-testid={`artist-card-${a.id}`}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
            className="group bg-[#0A0A0B] text-left p-6 flex flex-col gap-5">
            <div className="overflow-hidden aspect-[4/5]">
              <img src={fileUrl(a.photo, 800)} alt={a.name} loading="lazy" decoding="async"
                className="img-bw w-full h-full object-cover group-hover:scale-105" />
            </div>
            <div>
              <h3 className="font-display uppercase text-3xl tracking-wider">{a.name}</h3>
              <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#E63946] mt-1">{pick(a.specialty)}</p>
            </div>
            <span className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-zinc-500 group-hover:text-white transition-colors">
              {t("artists_view_gallery")} →
            </span>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setActive(null)} data-testid="artist-modal">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#121214] border border-white/10 max-w-4xl w-full max-h-[85vh] overflow-y-auto grid md:grid-cols-2"
              onClick={(e) => e.stopPropagation()}>
              <img src={fileUrl(active.photo, 1200)} alt={active.name} decoding="async" className="w-full h-64 md:h-full object-cover img-bw" />
              <div className="p-8 flex flex-col gap-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display uppercase text-4xl tracking-wider">{active.name}</h3>
                    <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#E63946] mt-2">{pick(active.specialty)}</p>
                  </div>
                  <button onClick={() => setActive(null)} data-testid="artist-modal-close" aria-label={t("close")}
                    className="border border-white/15 p-2 hover:bg-white hover:text-black transition-colors">
                    <X size={16} />
                  </button>
                </div>
                <p className="text-zinc-300 leading-relaxed">{pick(active.bio)}</p>
                {active.instagram && (
                  <a href={active.instagram} target="_blank" rel="noreferrer" data-testid="artist-instagram-link"
                    className="inline-flex items-center gap-2 font-mono2 text-[11px] tracking-[0.25em] uppercase text-zinc-400 hover:text-white transition-colors">
                    <Instagram size={14} /> Instagram
                  </a>
                )}
                {works.length > 0 && (
                  <div>
                    <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">{t("artists_works")}</p>
                    <div className="grid grid-cols-3 gap-2">
                      {works.map((w, i) => (
                        <button key={w.id} onClick={() => setWork(i)} data-testid={`artist-work-${w.id}`}
                          className="overflow-hidden border border-white/10 hover:border-white/50 transition-colors">
                          <Thumb src={w.image} alt={w.title} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {settings?.widget_embed_code && (
                  <div className="border-t border-white/10 pt-5" data-testid="artist-booking-widget">
                    <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">{t("artist_book")}</p>
                    <WidgetEmbed code={settings.widget_embed_code} bare />
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {current && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/95 flex items-center justify-center p-4"
            onClick={() => setWork(null)} data-testid="artist-work-lightbox">
            {isVideo(current.image) ? (
              <video src={fileUrl(current.image)} controls autoPlay muted loop playsInline
                className="max-h-[85vh] max-w-full" onClick={(e) => e.stopPropagation()} />
            ) : (
              <motion.img key={current.id} src={fileUrl(current.image)} alt={current.title}
                initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="max-h-[85vh] max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
            )}
            {works.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); setWork((work - 1 + works.length) % works.length); }}
                  data-testid="artist-work-prev" aria-label="anterior"
                  className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 border border-white/20 bg-black/50 p-3 hover:bg-white hover:text-black transition-colors">
                  <ChevronLeft size={20} />
                </button>
                <button onClick={(e) => { e.stopPropagation(); setWork((work + 1) % works.length); }}
                  data-testid="artist-work-next" aria-label="próxima"
                  className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 border border-white/20 bg-black/50 p-3 hover:bg-white hover:text-black transition-colors">
                  <ChevronRight size={20} />
                </button>
                <span className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono2 text-[10px] tracking-[0.3em] text-zinc-400">
                  {work + 1} / {works.length}
                </span>
              </>
            )}
            <button onClick={() => setWork(null)} data-testid="artist-work-close" aria-label={t("close")}
              className="absolute top-5 right-5 border border-white/20 bg-black/50 p-2 hover:bg-white hover:text-black transition-colors">
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
